import React, { useEffect } from 'react';
import MainTemplate from '../components/MainTemplate/MainTemplate';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Card, CardContent, Typography, Button, Stack, Chip, useTheme, useMediaQuery, CircularProgress } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import CheckCircleTwoToneIcon from '@mui/icons-material/CheckCircleTwoTone';
import HelpOutlineTwoToneIcon from '@mui/icons-material/HelpOutlineTwoTone';
import TimerTwoToneIcon from '@mui/icons-material/TimerTwoTone';
import ShieldTwoToneIcon from '@mui/icons-material/ShieldTwoTone';
import { checkCodeofConduct } from '../utils/helper-functions.js';
import useParentHistory from '../hooks/useParentHistory';
import { setParentHistory } from '../features/parentInfo/parentInfoSlice';
import LoadingBackdrop from '../components/LoadingBackdrop/LoadingBackdrop';

const Introduction = () => {
    const { college, SessionID, ParentID: authParentID } = useSelector((state) => state.auth);
    const dispatch = useDispatch();
    const location = useLocation();
    const navState = location.state;
    const parentCode = navState?.ParentID || authParentID;
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Prefetch parent history here so Parent Information renders without a second wait.
    const {
        data: parentHistoryData,
        isSuccess: isParentHistorySuccess,
        isError: isParentHistoryError,
    } = useParentHistory(parentCode);

    useEffect(() => {
        if (isParentHistorySuccess && parentHistoryData != null) {
            dispatch(setParentHistory(parentHistoryData));
        }
    }, [isParentHistorySuccess, parentHistoryData, dispatch]);

    const isParentHistoryLoading =
        Boolean(parentCode) && !isParentHistorySuccess && !isParentHistoryError;
    const showParentHistoryLoader = isParentHistoryLoading;

    const steps = [
        {
            number: 1,
            icon: "🧾",
            title: "Parent Code of Conduct",
            details: [
                "You will be required to read and acknowledge the school's Parent Code of Conduct.",
                "Tick the checkbox to confirm your acceptance before proceeding.",
                "An acknowledgment email will also be sent to your registered email address."
            ]
        },
        {
            number: 2,
            icon: "👨‍👩‍👧",
            title: "Parent & Student Information",
            details: [
                "Review and update any information that is missing or incorrect.",
                "Some fields may be pre-filled from our TASS system. Please verify their accuracy.",
                "Ensure all mandatory fields are completed before proceeding.",
                "Please select the acknowledgment tick box before proceeding."
            ]
        },
        {
            number: 3,
            icon: "🎓",
            title: "Select Discounts",
            details: [
                "Select the discount category you wish to apply for (e.g. Health Care Card / Pensioner Concession).",
                "After selecting discounts and acknowledging the Terms & Conditions, proceed to the Fee Structure page."
            ]
        },
        {
            number: 4,
            icon: "💰",
            title: "Validate Re-enrolment Fees",
            details: [
                "Based on your selections and child’s year level, the system calculates the Re-enrolment Fee.",
                "Review the fee summary for accuracy before proceeding.",
                "Select your preferred mode of remaining payment and click Next to continue."
            ]
        },
        {
            number: 5,
            icon: "📋",
            title: "Make the Payment",
            details: [
                "Proceed to the payment section once you’ve validated your fee structure.",
                "You’ll be redirected to our secure payment gateway (BPOINT) to finalise your payment."
            ]
        },
        {
            number: 6,
            icon: "💳",
            title: "Credit Card Payments",
            details: [
                "On the Payment page, enter your credit/debit card details (card number, expiry date, CVV).",
                "Make sure the payment is successful before closing the window.",
                "A receipt will be sent once the payment is confirmed."
            ]
        },
        {
            number: 7,
            icon: "✅",
            title: "Payment Complete Page",
            details: [
                "After successful payment, you will land on the Payment Confirmation Page.",
                "This confirms that your child’s re-enrolment has been successfully completed.",
                "A confirmation email will also be sent to your registered address."
            ]
        },
    ];

    // Owing this year no longer stops a family here. What each campus is still owed is collected
    // on the payment page instead, inside that campus's minimum, so re-enrolling settles it.
    const handleContinue = async () => {
        const res = await checkCodeofConduct(navState?.ParentID);
        if (res) {
            return navigate(`/parentinformation`, { state: navState });
        } else {
            return navigate(`/code-of-conduct`, { state: navState });
        }
    };

    const content = (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-start',
                width: '100%',
                bgcolor: '#F8FAFC',
                px: { xs: 0, sm: 2, md: 3 },
                py: { xs: 1, sm: 3 },
            }}
        >
            <Card
                sx={{
                    width: '100%',
                    maxWidth: { xs: '100%', lg: 1050 },
                    borderRadius: { xs: '12px', sm: '16px' },
                    boxShadow: { xs: '0 2px 10px rgba(0, 0, 0, 0.04)', sm: '0 10px 30px rgba(0, 0, 0, 0.05)' },
                    border: '1px solid #e2e8f0',
                    overflow: 'hidden',
                    bgcolor: '#FFFFFF',
                }}
            >
                <CardContent
                    sx={{
                        p: { xs: 1.5, sm: 3, md: 4 },
                        '&:last-child': { pb: { xs: 2, sm: 3, md: 4 } }
                    }}
                >

                    {navState?.resumePayment && (
                        <Box
                            sx={{
                                mb: { xs: 2, sm: 3 },
                                p: { xs: 1.5, sm: 2 },
                                borderRadius: 2,
                                bgcolor: '#FFFBEB',
                                border: '1px solid #FDE68A',
                            }}
                        >
                            <Typography sx={{ fontWeight: 700, color: '#92400E', fontSize: { xs: '13.5px', sm: '15px' }, mb: 0.5 }}>
                                A campus payment is still outstanding
                            </Typography>
                            <Typography sx={{ color: '#B45309', fontSize: { xs: '12.5px', sm: '14px' }, lineHeight: 1.5 }}>
                                One of your campuses has been paid, but another still has fees to settle. Continue
                                through the steps below to reach the payment page and pay the remaining charges.
                            </Typography>
                        </Box>
                    )}

                    {/* Header Banner */}
                    <Box sx={{ mb: { xs: 2.5, sm: 3.5 }, textAlign: 'center' }}>
                        <Chip
                            icon={<ShieldTwoToneIcon style={{ color: '#059669', fontSize: 18 }} />}
                            label="Official Re-enrolment Portal"
                            sx={{
                                bgcolor: '#ECFDF5',
                                color: '#065F46',
                                fontWeight: 600,
                                fontSize: '13px',
                                mb: 1.5,
                                px: 1,
                                py: 0.5,
                                borderRadius: '20px',
                                border: '1px solid #A7F3D0'
                            }}
                        />
                        <Typography
                            variant="h4"
                            component="h1"
                            sx={{
                                fontWeight: 800,
                                color: '#0F172A',
                                fontSize: { xs: '1.4rem', sm: '2rem', md: '2.25rem' },
                                letterSpacing: '-0.02em',
                                mb: 1
                            }}
                        >
                            Re-enrolment Instructions & Guide
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                color: '#64748B',
                                fontSize: { xs: '13.5px', sm: '16px' },
                                maxWidth: 720,
                                mx: 'auto',
                                lineHeight: 1.5
                            }}
                        >
                            Welcome to the Australian Islamic College annual re-enrolment guide. Please review the video tutorial and step-by-step instructions below before getting started.
                        </Typography>

                        {/* Feature Badges */}
                        <Stack
                            direction="row"
                            spacing={{ xs: 1, sm: 2 }}
                            justifyContent="center"
                            flexWrap="wrap"
                            useFlexGap
                            sx={{ mt: 2 }}
                        >
                            <Chip
                                icon={<TimerTwoToneIcon style={{ color: '#2563EB', fontSize: 16 }} />}
                                label="Takes ~5 Minutes"
                                size="small"
                                sx={{ bgcolor: '#EFF6FF', color: '#1E40AF', fontWeight: 500, fontSize: '12px' }}
                            />
                            <Chip
                                icon={<CheckCircleTwoToneIcon style={{ color: '#059669', fontSize: 16 }} />}
                                label="7 Simple Steps"
                                size="small"
                                sx={{ bgcolor: '#F0FDF4', color: '#166534', fontWeight: 500, fontSize: '12px' }}
                            />
                            <Chip
                                icon={<ShieldTwoToneIcon style={{ color: '#7C3AED', fontSize: 16 }} />}
                                label="Secure Payment via BPOINT"
                                size="small"
                                sx={{ bgcolor: '#F5F3FF', color: '#5B21B6', fontWeight: 500, fontSize: '12px' }}
                            />
                        </Stack>
                    </Box>

                    {/* Video Section */}
                    <Box
                        sx={{
                            borderRadius: { xs: 2.5, sm: 3 },
                            overflow: 'hidden',
                            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.08)',
                            border: '1px solid #E2E8F0',
                            mb: { xs: 3, sm: 4 },
                            bgcolor: '#0F172A',
                        }}
                    >
                        <Box
                            sx={{
                                px: { xs: 1.5, sm: 2 },
                                py: 1.25,
                                bgcolor: '#1E293B',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                borderBottom: '1px solid #334155'
                            }}
                        >
                            <PlayCircleOutlineIcon sx={{ color: '#34D399', fontSize: 20 }} />
                            <Typography variant="subtitle2" sx={{ color: '#F8FAFC', fontWeight: 600, fontSize: { xs: '12px', sm: '13px' } }}>
                                Video Tutorial: How to Complete Your Re-enrolment
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                position: 'relative',
                                paddingTop: '56.25%', /* 16:9 Aspect Ratio */
                            }}
                        >
                            {/* Vimeo, and nothing but the video.
                                title=0, byline=0 and portrait=0 drop Vimeo's own heading, the
                                uploader's name and their avatar, which would otherwise sit over a
                                parent's first screen. dnt=1 asks Vimeo not to track the viewer —
                                these are families, and they did not choose Vimeo.
                                The 56.25% wrapper above is right for this video: Vimeo reports it
                                as 426x240, which is 56.34%. */}
                            <iframe
                                src="https://player.vimeo.com/video/1231974413?title=0&byline=0&portrait=0&dnt=1"
                                title="Re-enrolment Tutorial Video"
                                allow="autoplay; fullscreen; picture-in-picture"
                                referrerPolicy="strict-origin-when-cross-origin"
                                allowFullScreen
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    border: 0,
                                }}
                            ></iframe>
                        </Box>
                    </Box>

                    {/* Step-by-Step Instructions Title */}
                    <Box sx={{ mb: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#0F172A', fontSize: { xs: '1.05rem', sm: '1.25rem' } }}>
                            Step-by-Step Process Overview
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 600 }}>
                            7 Steps
                        </Typography>
                    </Box>

                    {/* Steps List */}
                    <Stack spacing={1.75} sx={{ mb: 3.5 }}>
                        {steps.map((step) => (
                            <Box
                                key={step.number}
                                sx={{
                                    p: { xs: 1.5, sm: 2.5 },
                                    borderRadius: { xs: 2.5, sm: 3 },
                                    bgcolor: '#F8FAFC',
                                    border: '1px solid #E2E8F0',
                                    transition: 'all 0.2s ease-in-out',
                                    '&:hover': {
                                        borderColor: '#CBD5E1',
                                        bgcolor: '#F1F5F9',
                                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.03)'
                                    }
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 1.25, sm: 2 } }}>
                                    {/* Step Number Badge */}
                                    <Box
                                        sx={{
                                            minWidth: { xs: 30, sm: 40 },
                                            height: { xs: 30, sm: 40 },
                                            borderRadius: '50%',
                                            background: 'linear-gradient(135deg, #059669 0%, #10B981 100%)',
                                            color: '#FFFFFF',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontWeight: 700,
                                            fontSize: { xs: '13px', sm: '16px' },
                                            boxShadow: '0 3px 8px rgba(16, 185, 129, 0.3)',
                                            flexShrink: 0,
                                            mt: 0.25
                                        }}
                                    >
                                        {step.number}
                                    </Box>

                                    {/* Step Content */}
                                    <Box sx={{ flex: 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75, flexWrap: 'wrap' }}>
                                            <span style={{ fontSize: '1.15rem' }}>{step.icon}</span>
                                            <Typography
                                                variant="subtitle1"
                                                sx={{
                                                    fontWeight: 700,
                                                    color: '#1E293B',
                                                    fontSize: { xs: '14.5px', sm: '16px' }
                                                }}
                                            >
                                                {step.title}
                                            </Typography>
                                        </Box>

                                        <Stack spacing={0.5}>
                                            {step.details.map((detail, i) => (
                                                <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                                                    <Box
                                                        component="span"
                                                        sx={{
                                                            width: 5,
                                                            height: 5,
                                                            borderRadius: '50%',
                                                            bgcolor: '#10B981',
                                                            mt: 0.85,
                                                            flexShrink: 0
                                                        }}
                                                    />
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: '#475569',
                                                            fontSize: { xs: '12.5px', sm: '14px' },
                                                            lineHeight: 1.45
                                                        }}
                                                    >
                                                        {detail}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Stack>
                                    </Box>
                                </Box>
                            </Box>
                        ))}
                    </Stack>

                    {/* Support & Assistance Card */}
                    <Box
                        sx={{
                            p: { xs: 2, sm: 3 },
                            borderRadius: { xs: 2.5, sm: 3 },
                            bgcolor: '#ECFDF5',
                            border: '1px solid #A7F3D0',
                            display: 'flex',
                            gap: { xs: 1.25, sm: 2 },
                            alignItems: 'flex-start',
                        }}
                    >
                        <HelpOutlineTwoToneIcon sx={{ color: '#059669', fontSize: { xs: 24, sm: 28 }, mt: 0.25, flexShrink: 0 }} />
                        <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#065F46', mb: 0.5, fontSize: { xs: '14px', sm: '15px' } }}>
                                Need Help During Re-enrolment?
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#047857', fontSize: { xs: '12.5px', sm: '14px' }, lineHeight: 1.5 }}>
                                If you have questions or encounter any issues, our Admin Officers, Enrolment Officers, and AR Team are ready to assist. You can also use the <b>WhatsApp Chat Bot</b> on screen for instant answers.
                            </Typography>
                        </Box>
                    </Box>

                    {/* Desktop Action Area */}
                    {!isMobile && (
                        <Box
                            sx={{
                                mt: 3,
                                pt: 2,
                                borderTop: '1px solid #E2E8F0',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <TimerTwoToneIcon sx={{ color: '#64748B', fontSize: 20 }} />
                                <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 500, fontSize: '13px' }}>
                                    Estimated time: ~5 minutes
                                </Typography>
                            </Box>
                            <Button
                                variant="contained"
                                size="large"
                                endIcon={<NavigateNextIcon />}
                                disabled={showParentHistoryLoader}
                                onClick={handleContinue}
                                sx={{
                                    bgcolor: '#059669',
                                    color: '#FFFFFF',
                                    fontWeight: 700,
                                    px: 4,
                                    py: 1.5,
                                    borderRadius: '12px',
                                    textTransform: 'none',
                                    fontSize: '16px',
                                    boxShadow: '0 4px 14px rgba(5, 150, 105, 0.35)',
                                    '&:hover': {
                                        bgcolor: '#047857',
                                        boxShadow: '0 6px 20px rgba(5, 150, 105, 0.45)',
                                    }
                                }}
                            >
                                Start Re-enrolment
                            </Button>
                        </Box>
                    )}
                </CardContent>
            </Card>

            {/* Mobile Sticky Glassmorphism Action Bar */}
            {isMobile && (
                <Box
                    sx={{
                        position: 'fixed',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        px: 2,
                        py: 1.5,
                        bgcolor: 'rgba(255, 255, 255, 0.94)',
                        backdropFilter: 'blur(16px)',
                        borderTop: '1px solid rgba(226, 232, 240, 0.9)',
                        boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.08)',
                        zIndex: 1100,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2
                    }}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Step 1 of 7
                        </Typography>
                        <Typography variant="subtitle2" sx={{ color: '#0F172A', fontWeight: 700, fontSize: '13px' }}>
                            Instructions
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        size="large"
                        endIcon={<NavigateNextIcon />}
                        disabled={showParentHistoryLoader}
                        onClick={handleContinue}
                        sx={{
                            bgcolor: '#059669',
                            color: '#FFFFFF',
                            fontWeight: 700,
                            px: 3,
                            py: 1.25,
                            borderRadius: '12px',
                            textTransform: 'none',
                            fontSize: '15px',
                            minHeight: '48px', // iOS 48px touch target
                            boxShadow: '0 4px 14px rgba(5, 150, 105, 0.35)',
                            '&:hover': {
                                bgcolor: '#047857',
                            }
                        }}
                    >
                        Start Re-enrolment
                    </Button>
                </Box>
            )}
        </Box>
    );

    return (
        <div>
            <LoadingBackdrop
                open={showParentHistoryLoader}
                icon={<CircularProgress sx={{ color: '#fff' }} />}
            />
            {!showParentHistoryLoader && (
                <MainTemplate college={`${college}`} content={content} SessionID={SessionID} />
            )}
        </div>
    );
};

export default Introduction;
